import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Typography,
  TextField,
  AnchorMenu,
  DateFilterModal,
  TableFooter,
  Checkbox,
  Button,
  Modal,
} from "components";
import debounce from "lodash.debounce";
import {
  isObjectsEqual,
  formatUrlQuery,
  formatFilterPayload,
  formatDateTimeExact,
  truncateString,
  capitalizeFirstLetter,
  setAlert,
} from "utils/helper";
import { IoMdClose } from "react-icons/io";
import { routes } from "routes/apiRoutes";
import { BiCalendar } from "react-icons/bi";
import { FaExchangeAlt } from "react-icons/fa";
import makeRequest from "services/makeRequest";
import DetailModal from "./DetailModal";

const ManageNotifications = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [resendingNotification, setResendingNotification] = useState(false);

  const columns = [
    {
      Header: " ",
      Cell: ({ row }) => (
        <Checkbox
          name={"selectedId"}
          checked={selectedIds.includes(row.original?.id)}
          onChange={(e) => {
            let checked = e.target.checked;
            let notificationId = row.original?.id;
            if (checked) {
              setSelectedIds([...selectedIds, notificationId]);
            } else {
              setSelectedIds(selectedIds.filter((id) => notificationId !== id));
            }
          }}
        />
      ),
    },
    {
      Header: "S/N",
      Cell: ({ row }) => Number(row.id) + 1,
    },
    {
      Header: "Name",
      Cell: ({ row }) =>
        capitalizeFirstLetter(row.original?.user?.firstName) +
        " " +
        capitalizeFirstLetter(row.original?.user?.lastName),
    },
    {
      Header: "UserName",
      accessor: "recipientUsername",
    },
    {
      Header: "Email Subject",
      accessor: "subject",
    },
    {
      Header: "Message",
      Cell: ({ row }) => truncateString(row.original?.message, 20),
    },
    {
      Header: "Email Address",
      accessor: "recipientAddress",
    },
    {
      Header: "Date Sent",
      accessor: "createdDate",
      Cell: (props) => formatDateTimeExact(props?.value),
    },

    {
      Header: "Action",
      Cell: ({ row }) => (
        <AnchorMenu anchorRender={() => anchorRender(row.original)} />
      ),
    },
  ];

  const [detailModalContent, setDetailModalContent] = useState(null);

  const onClose = () => {
    setOpenConfirmationModal(false);
    setSelectedIds([]);
  };

  const resendNotification = async () => {
    if (selectedIds.length > 30)
      return setAlert("Maximum of 30 selections allowed");
    setResendingNotification(true);
    try {
      const { data } = await makeRequest("post", routes.notificationsMgt(), {
        ids: selectedIds.map((selectedId) => Number(selectedId)),
      });
      setOpenConfirmationModal(false);
      setSelectedIds([]);
      setAlert(data.responseMessage, "success");
    } catch (err) {
      console.log(err);
    } finally {
      setResendingNotification(false);
    }
  };

  const anchorRender = (value) => {
    return (
      <ul className="py-1" style={{ paddingLeft: "1rem" }}>
        <li
          onClick={() => setDetailModalContent(value)}
          className="cursor-pointer"
        >
          <Typography state="primary" variant="body3">
            View Message
          </Typography>
        </li>
        <li
          onClick={() => {
            setSelectedIds([value.id]);
            setOpenConfirmationModal(true);
          }}
          className="cursor-pointer"
        >
          <Typography
            state={resendingNotification ? "grey" : "primary"}
            variant="body3"
          >
            Resend message
          </Typography>
        </li>
      </ul>
    );
  };

  const initialFilterState = {
    sort: true,
    number: 1,
    createdDateFrom: "",
    createdDateTo: "",
    address: "",
    subject: "",
    size: 10,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const [data, setData] = useState({
    notifications: null,
    loading: false,
  });

  const {
    createdDateFrom,
    createdDateTo,
    address,
    subject,
    size,
    number,
    sort,
  } = filterState;

  const [openDateModal, setOpenDateModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "size") {
      filterState.number = 1;
    }
    setFilterState({
      ...filterState,
      [name]: name === "sort" ? JSON.parse(value) : value,
    });
  };

  const getNotificationsData = (queryParams) =>
    makeRequest("get", routes.notificationsMgt(queryParams));

  const resetFilter = () => {
    filterState.address = "";
    setFilterState(initialFilterState);

    loadNotifications();
  };

  const loadNotifications = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setData({ ...data, loading: true });
    try {
      const res = await getNotificationsData(queryParams);
      setData((prev) => ({
        ...prev,
        notifications: res.data.data,
        loading: false,
      }));
    } catch (err) {
      console.log(err);
      setData({ ...data, loading: false });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadWithDebounce = useCallback(
    debounce((address) => {
      if (address === "") {
        loadNotifications();
      } else {
        loadNotifications(`address=${address}`);
      }
    }, 1000),
    []
  );

  const handleSearch = (e) => {
    let val = e.target.value;
    setFilterState({ ...filterState, address: val });
    loadWithDebounce(val);
  };

  useEffect(() => {
    loadNotifications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, size, subject, sort]);

  return (
    <div className="dashboard-page-container">
      <Typography variant="heading3" component="h3" state="primary">
        Notifications
      </Typography>
      <div className="table-header-actions mt-4 mb-2">
        <div className="d-md-flex cursor-pointer">
          <div className="filter-cta d-none d-sm-block mb-2">
            <Typography state="grey" className={"mt-1"}>
              Filter By
            </Typography>
          </div>
          <select
            name="subject"
            value={subject}
            className="table-header-select mb-2"
            onChange={handleChange}
          >
            <option value={""} disabled>
              Email Subject
            </option>
            <option value={"Registration Confirmation"}>
              Registration Confirmation
            </option>
            <option value={"Reset Password"}>Reset Password</option>
          </select>
          <select
            name="sort"
            value={sort}
            className="table-header-select mb-2"
            onChange={handleChange}
          >
            <option value={false}>Most Recent</option>
            <option value={true}>Least Recent</option>
          </select>
          <div className="date-cta mb-2" onClick={() => setOpenDateModal(true)}>
            <Typography component="span">Date Sent</Typography>{" "}
            <BiCalendar style={{ marginBottom: ".2rem" }} />
          </div>
          {!isObjectsEqual(initialFilterState, filterState) && (
            <div className="reset-filter-cta mb-2" onClick={resetFilter}>
              <Typography state="grey" component="span">
                Reset Filter
              </Typography>{" "}
              <FaExchangeAlt
                style={{
                  color: "grey",
                  fontSize: "14px",
                  marginLeft: ".3rem",
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between my-3">
        <div>
          <TextField
            name="address"
            id="address"
            value={address}
            onChange={handleSearch}
            placeholder={"Search by email address"}
            size="sm"
            required={false}
            searchField
            className="tiny-bordered-textfield"
          />
        </div>
        <Button
          size="xs"
          label="Resend All"
          variant="secondary"
          disabled={!selectedIds.length}
          onClick={() => setOpenConfirmationModal(true)}
        />
      </div>
      <Table
        columns={columns}
        data={data.notifications?.content}
        centerData
        loading={data.loading}
      />
      {data.notifications?.content?.length ? (
        <TableFooter
          pageSize={filterState.size}
          handleChange={handleChange}
          availableDataLength={data.notifications.content.length}
          totalDataLength={data.notifications.totalElements}
          handlePageClick={(pageNo) =>
            setFilterState({ ...filterState, number: pageNo })
          }
        />
      ) : (
        ""
      )}
      <DateFilterModal
        openDateModal={openDateModal}
        setOpenDateModal={setOpenDateModal}
        handleChange={handleChange}
        startDateName={"createdDateFrom"}
        startDate={createdDateFrom}
        endDateName={"createdDateTo"}
        endDate={createdDateTo}
        onButtonClick={() => {
          setOpenDateModal(false);
          loadNotifications();
        }}
      />
      <DetailModal
        open={!!detailModalContent}
        onClose={() => setDetailModalContent(null)}
        detail={detailModalContent}
      />
      <Modal showModal={openConfirmationModal} onClose={onClose}>
        <div className="my-4">
          <div className="d-flex justify-content-end">
            <IoMdClose className="cursor-pointer" onClick={onClose} />
          </div>
          <div className="mt-2 mx-auto" style={{ width: "90%" }}>
            <div className="text-center ">
              <Typography variant="subtitle3" state="primary">
                Confirmation
              </Typography>
              <Typography className="my-4" variant="body1" state="primary">
                Are you sure you want to resend{" "}
                {selectedIds.length > 1
                  ? "these notifications"
                  : "notification"}
                ?
              </Typography>
              <div className="d-flex justify-content-between pt-3">
                <Button
                  size="xs"
                  label="Cancel"
                  variant="secondary"
                  disabled={resendingNotification}
                  onClick={onClose}
                />
                <Button
                  size="xs"
                  label={resendingNotification ? "Sending..." : "Resend"}
                  style={{ marginLeft: "1rem" }}
                  disabled={resendingNotification}
                  onClick={resendNotification}
                  type="submit"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManageNotifications;
